// Guardrail: Veera outputs must remain descriptive and non-directive.
// Do NOT add scoring, evaluation, or behavior direction here.
// Coach-first rule: Leader-facing outputs must be downstream of coach mediation.
import type { LeaderChatActionPolicy } from "../../supabase/functions/_shared/leaderChatActionPolicy";
import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { readSSEStream } from './useSSEStream';
import { useNavigate } from "react-router-dom";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { PREVIEW_VEERA_CONVERSATION } from "@/data/leaderPreviewData";
import { PREVIEW_LEADER_USER_ID } from "@/preview/leaderPreviewContext";
import { getVeeraContextualOpenerSeed } from "@/lib/veeraContextualOpenerSeed";

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: Date;
  persisted?: boolean;
  // Only an unresolved local delivery marker, never a server persistence receipt.
  delivery?: { sessionId: string; afterMessageId: string | null };
}

export interface ChatDeliveryRecoveryState {
  required: boolean;
  reason?: "history_unavailable" | "stream_incomplete";
  checking: boolean;
  check: () => Promise<void>;
}

type PendingDelivery = {
  sessionId: string;
  clientMessageId: string;
  question: string;
  afterMessageId: string | null;
};

interface LeaderChatTurn {
  role: "user" | "assistant";
  content: string;
}

type LeaderChatMessagesTableClient = {
  select: (
    columns: string,
  ) => {
    eq: (
      column: string,
      value: string,
    ) => {
      eq: (
        column: string,
        value: string,
      ) => {
        order: (
          column: string,
          options: { ascending: boolean },
        ) => Promise<{
          data: Array<{
            id: string;
            role: "user" | "assistant";
            content: string;
            created_at: string;
          }> | null;
          error: { message?: string } | null;
        }>;
      };
    };
  };
};

interface PageContext {
  pageName: string;
  data?: Record<string, unknown>;
}

interface LeaderContextualEntry {
  sourceLabel?: string | null;
  assistantOpener?: {
    sourceLabel?: string | null;
    carryOverSummary?: string | null;
    whyItMattersNow?: string | null;
    question?: string | null;
    shouldAskQuestion?: boolean;
  } | null;
}

export type LeaderHistoryScope = "recent" | "rolling_30d" | "rolling_60d" | "rolling_90d" | "rolling_365d" | "full";

interface VeeraAction {
  type: string;
  payload: Record<string, unknown>;
}

export interface LeaderConversationState {
  sessionId: string;
  surfaceRole: "leader";
  continuityFocus: string | null;
  confidence: "low" | "medium" | "high";
  freshness: "fresh" | "recent" | "stale" | "unknown" | null;
  stateVersion: number;
}

const SESSION_STORAGE_KEY = "veera-chat-session-id";
const MESSAGES_STORAGE_KEY = "veera-chat-messages";

const getStorageKey = (baseKey: string, userId: string, conversationId?: string) => (
  `${baseKey}${conversationId ? `-${conversationId}` : ""}-user-${encodeURIComponent(userId)}`
);

const getLegacyStorageKey = (baseKey: string, conversationId?: string) => (
  conversationId ? `${baseKey}-${conversationId}` : baseKey
);

const hydrateMessages = (raw: string | null): ChatMessage[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Array<Omit<ChatMessage, "createdAt"> & { createdAt: string }>;
    return parsed.map((message) => ({
      ...message,
      persisted: false,
      createdAt: new Date(message.createdAt),
    }));
  } catch {
    return [];
  }
};

const readEphemeralChatStorage = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeEphemeralChatStorage = (key: string, value: string) => {
  try {
    window.sessionStorage.setItem(key, value);
    window.localStorage.removeItem(key);
  } catch {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore storage cleanup failures in privacy-restricted browsers.
    }
  }
};

const removeEphemeralChatStorage = (key: string) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures in privacy-restricted browsers.
  }
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage cleanup failures in privacy-restricted browsers.
  }
};

const getLeaderChatMessagesTable = () => (
  supabase.from("leader_chat_messages") as unknown as LeaderChatMessagesTableClient
);

export function useLeaderChat(
  pageContext?: PageContext,
  historyScope: LeaderHistoryScope = "recent",
  conversationId?: string,
  actionPolicy: LeaderChatActionPolicy = "assist",
) {
  const { user, loading: authLoading } = useAuth();
  const authorizedUserScopeRef = useRef(user);
  useLayoutEffect(() => {
    if (user) {
      authorizedUserScopeRef.current = user;
    } else if (!authLoading) {
      authorizedUserScopeRef.current = null;
    }
  }, [authLoading, user]);
  const scopedUser = user ?? (authLoading ? authorizedUserScopeRef.current : null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conversationState, setConversationState] = useState<LeaderConversationState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streamingContent, setStreamingContent] = useState<string>('');
  const [isStreaming, setIsStreaming] = useState(false);
  const streamingContentRef = useRef('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestGenerationRef = useRef(0);
  const seededContextualConversationRef = useRef<string | null>(null);
  const [hasResolvedInitialMessages, setHasResolvedInitialMessages] = useState(false);
  const [recoveryRequired, setRecoveryRequired] = useState(false);
  const [checkingRecovery, setCheckingRecovery] = useState(false);
  const [historyReadFailed, setHistoryReadFailed] = useState(false);
  const historyReadyRef = useRef(false);
  const recoveredQuestionRef = useRef<string | null>(null);
  const pendingDeliveryRef = useRef<PendingDelivery | null>(null);
  const sendInFlightRef = useRef(false);
  const recoveryInFlightRef = useRef(false);
  const initialHistoryRef = useRef<Promise<void>>(Promise.resolve());
  const messagesRef = useRef(messages);
  const sessionIdRef = useRef(sessionId);
  useLayoutEffect(() => { messagesRef.current = messages; sessionIdRef.current = sessionId; }, [messages, sessionId]);
  const supabaseUrl = SUPABASE_URL;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useLayoutEffect(() => {
    return () => {
      requestGenerationRef.current += 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, []);

  const isPreview = scopedUser?.id === PREVIEW_LEADER_USER_ID;
  const sessionStorageKey = scopedUser?.id && !isPreview
    ? getStorageKey(SESSION_STORAGE_KEY, scopedUser.id, conversationId)
    : null;
  const messagesStorageKey = scopedUser?.id && !isPreview
    ? getStorageKey(MESSAGES_STORAGE_KEY, scopedUser.id, conversationId)
    : null;
  const legacySessionStorageKey = getLegacyStorageKey(SESSION_STORAGE_KEY, conversationId);
  const legacyMessagesStorageKey = getLegacyStorageKey(MESSAGES_STORAGE_KEY, conversationId);
  const activeConversationScopeRef = useRef<string | null>(messagesStorageKey);
  const hydratedSessionKeyRef = useRef<string | null>(null);
  const hydratedMessagesKeyRef = useRef<string | null>(null);
  const skipNextSessionPersistRef = useRef(false);
  const skipNextMessagesPersistRef = useRef(false);

  useLayoutEffect(() => {
    requestGenerationRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    streamingContentRef.current = '';
    seededContextualConversationRef.current = null;
    activeConversationScopeRef.current = messagesStorageKey;
    hydratedSessionKeyRef.current = null;
    hydratedMessagesKeyRef.current = null;
    skipNextSessionPersistRef.current = true;
    skipNextMessagesPersistRef.current = true;
    setMessages([]);
    setSessionId(null);
    setConversationState(null);
    setIsLoading(false);
    setIsStreaming(false);
    setStreamingContent('');
    setError(null);
    setHasResolvedInitialMessages(false);
    pendingDeliveryRef.current = null;
    historyReadyRef.current = false;
    recoveredQuestionRef.current = null;
    setHistoryReadFailed(false);
    sendInFlightRef.current = false;
    recoveryInFlightRef.current = false;
    setRecoveryRequired(false);
    setCheckingRecovery(false);
    messagesRef.current = [];
    sessionIdRef.current = null;

    if (isPreview) {
      setSessionId(conversationId || "preview-veera-thread");
      setMessages(PREVIEW_VEERA_CONVERSATION.map((message, index) => ({
        id: message.id,
        role: message.role,
        content: message.content,
        createdAt: new Date(`2026-03-30T1${index}:00:00.000Z`),
      })));
      setHasResolvedInitialMessages(true);
      return;
    }

    if (!scopedUser?.id || !sessionStorageKey || !messagesStorageKey || typeof window === "undefined") return;
    removeEphemeralChatStorage(legacySessionStorageKey);
    removeEphemeralChatStorage(legacyMessagesStorageKey);
    const restoredSession = conversationId || readEphemeralChatStorage(sessionStorageKey) || null;
    const restoredMessages = hydrateMessages(readEphemeralChatStorage(messagesStorageKey));
    const unresolved = restoredMessages.find(message => message.role === "user" && message.delivery?.sessionId === restoredSession);
    if (unresolved?.delivery) {
      pendingDeliveryRef.current = { sessionId: unresolved.delivery.sessionId, clientMessageId: unresolved.id, question: unresolved.content, afterMessageId: unresolved.delivery.afterMessageId };
      setRecoveryRequired(true);
      setError("stream_incomplete");
    }
    sessionIdRef.current = restoredSession;
    messagesRef.current = restoredMessages;
    setSessionId(restoredSession);
    setMessages(restoredMessages);
    hydratedSessionKeyRef.current = sessionStorageKey;
    hydratedMessagesKeyRef.current = messagesStorageKey;
  }, [
    conversationId,
    isPreview,
    legacyMessagesStorageKey,
    legacySessionStorageKey,
    messagesStorageKey,
    sessionStorageKey,
    scopedUser?.id,
  ]);

  // Both mount and recovery use the same owner-scoped, exact-session read path.
  const loadPersistedMessages = useCallback(async () => {
    const targetSession = sessionIdRef.current;
    if (isPreview || !scopedUser?.id || !targetSession) return true;
    const generation = requestGenerationRef.current;
    const scope = messagesStorageKey;
    const pending = pendingDeliveryRef.current;
    try {
      const { data, error: readError } = await getLeaderChatMessagesTable()
        .select("id, role, content, created_at")
        .eq("leader_id", scopedUser.id)
        .eq("session_id", targetSession)
        .order("created_at", { ascending: true });
      if (generation !== requestGenerationRef.current || scope !== activeConversationScopeRef.current || pending !== pendingDeliveryRef.current) return false;
      if (readError || !data) return false;
      if (pending) {
        const anchor = pending.afterMessageId ? data.findIndex(row => row.id === pending.afterMessageId) : -1;
        if (pending.afterMessageId && anchor < 0) return false;
        const tail = data.slice(anchor + 1);
        // Missing, partial or ambiguous results cannot authorize another send.
        if (tail.length !== 2 || tail[0].role !== "user" || tail[0].content !== pending.question || tail[1].role !== "assistant") return false;
        recoveredQuestionRef.current = pending.question;
        pendingDeliveryRef.current = null;
        setRecoveryRequired(false);
        setError(null);
      }
      const hydrated = data.map(message => ({ id: message.id, role: message.role, content: message.content, createdAt: new Date(message.created_at), persisted: true }));
      if (hydrated.length) {
        messagesRef.current = hydrated;
        setMessages(hydrated);
      }
      return true;
    } catch {
      // A failed read leaves uncertain delivery blocked. It never sends a model request.
      return false;
    }
  }, [isPreview, scopedUser?.id, messagesStorageKey]);

  useEffect(() => {
    let cancelled = false;
    const generation = requestGenerationRef.current;
    const loading = loadPersistedMessages().then(ready => {
      if (cancelled || generation !== requestGenerationRef.current) return;
      historyReadyRef.current = ready;
      setHasResolvedInitialMessages(ready);
      setHistoryReadFailed(!ready);
      if (!ready && !pendingDeliveryRef.current) setError("history_unavailable");
    });
    initialHistoryRef.current = loading;
    return () => { cancelled = true; };
  }, [loadPersistedMessages]);

  const checkSavedConversation = useCallback(async () => {
    if ((!pendingDeliveryRef.current && historyReadyRef.current) || sendInFlightRef.current || recoveryInFlightRef.current) return;
    recoveryInFlightRef.current = true;
    setCheckingRecovery(true);
    const generation = requestGenerationRef.current;
    try {
      const ready = await loadPersistedMessages();
      if (generation === requestGenerationRef.current) {
        historyReadyRef.current = ready;
        setHasResolvedInitialMessages(ready);
        setHistoryReadFailed(!ready);
        if (ready) setError(null);
      }
    }
    finally {
      if (generation === requestGenerationRef.current) {
        recoveryInFlightRef.current = false;
        setCheckingRecovery(false);
      }
    }
  }, [loadPersistedMessages]);

  useEffect(() => {
    if (isPreview) return;
    if (!sessionStorageKey || typeof window === "undefined") return;
    if (hydratedSessionKeyRef.current !== sessionStorageKey) return;
    if (skipNextSessionPersistRef.current) {
      skipNextSessionPersistRef.current = false;
      return;
    }
    if (!sessionId) return;
    writeEphemeralChatStorage(sessionStorageKey, sessionId);
  }, [isPreview, sessionId, sessionStorageKey]);

  useEffect(() => {
    if (isPreview) return;
    if (!messagesStorageKey || typeof window === "undefined") return;
    if (hydratedMessagesKeyRef.current !== messagesStorageKey) return;
    if (skipNextMessagesPersistRef.current) {
      skipNextMessagesPersistRef.current = false;
      return;
    }
    writeEphemeralChatStorage(
      messagesStorageKey,
      JSON.stringify(
        messages.map((message) => ({
          ...message,
          createdAt: message.createdAt.toISOString(),
        })),
      ),
    );
  }, [isPreview, messages, messagesStorageKey]);

  const contextualEntry = (pageContext?.data as { contextualEntry?: LeaderContextualEntry | null } | undefined)?.contextualEntry ?? null;

  useEffect(() => {
    if (!scopedUser?.id || isPreview) return;
    const contextualSeed = getVeeraContextualOpenerSeed({
      hasResolvedInitialMessages,
      messagesLength: messages.length,
      conversationId,
      sessionId,
      seededConversationId: seededContextualConversationRef.current,
      contextualEntry,
    });
    if (!contextualSeed) return;

    seededContextualConversationRef.current = contextualSeed.conversationSeedKey;
    setMessages([
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content: contextualSeed.assistantContent,
        createdAt: new Date(),
      },
    ]);

    if (!sessionId && conversationId) {
      setSessionId(conversationId);
    }
  }, [conversationId, contextualEntry, hasResolvedInitialMessages, isPreview, messages.length, scopedUser?.id, sessionId]);

  const handleActions = useCallback((actions: VeeraAction[]) => {
    for (const action of actions) {
      switch (action.type) {
        case "navigate":
          if (action.payload.path) {
            navigate(action.payload.path as string);
          }
          break;
        case "meeting_created":
        case "meeting_selection_changed":
        case "nudge_scheduled":
        case "nudge_removed":
          queryClient.invalidateQueries({ queryKey: ["weekly-moments"] });
          queryClient.invalidateQueries({ queryKey: ["today-schedule"] });
          queryClient.invalidateQueries({ queryKey: ["meetings"] });
          queryClient.invalidateQueries({ queryKey: ["nudges"] });
          break;
      }
    }
  }, [navigate, queryClient]);

  const sendMessage = useCallback(async (content: string) => {
    if (authLoading || !user || !content.trim() || sendInFlightRef.current || pendingDeliveryRef.current) return;
    if (user.id === PREVIEW_LEADER_USER_ID) return;
    if (!messagesStorageKey) return;
    sendInFlightRef.current = true;
    const submittedContent = content.trim();
    const submittedPolicy = actionPolicy;
    const requestScope = messagesStorageKey;
    const admissionGeneration = requestGenerationRef.current;
    await initialHistoryRef.current;
    if (activeConversationScopeRef.current !== requestScope || requestGenerationRef.current !== admissionGeneration) return;
    if (!historyReadyRef.current) { sendInFlightRef.current = false; return; }
    requestGenerationRef.current += 1;
    const requestGeneration = requestGenerationRef.current;
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const isCurrentRequest = () => (
      requestGenerationRef.current === requestGeneration
      && activeConversationScopeRef.current === requestScope
    );
    const recentMessages: LeaderChatTurn[] = messagesRef.current
      .slice(-8)
      .map((message) => ({
        role: message.role,
        content: message.content,
      }))
      .filter((message) => message.content.trim().length > 0);

    setError(null);
    setIsLoading(true);

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: content.trim(),
      createdAt: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    setStreamingContent('');
    streamingContentRef.current = '';
    let hasPersistenceReceipt = false;
    let receivedMetadata = false;
    let dispatched = false;
    let serverError = false;
    let confirmedUserMessageId: string | undefined;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!isCurrentRequest()) return;
      if (!session) throw new Error('No session');

      const requestSession = sessionIdRef.current || crypto.randomUUID();
      sessionIdRef.current = requestSession;
      setSessionId(requestSession);
      if (sessionStorageKey) writeEphemeralChatStorage(sessionStorageKey, requestSession);
      const afterMessageId = messagesRef.current.filter(message => message.persisted).at(-1)?.id ?? null;
      pendingDeliveryRef.current = { sessionId: requestSession, clientMessageId: userMessage.id, question: submittedContent, afterMessageId };
      const withDelivery = [...messagesRef.current.filter(message => message.id !== userMessage.id), { ...userMessage, delivery: { sessionId: requestSession, afterMessageId } }];
      messagesRef.current = withDelivery;
      setMessages(withDelivery);
      writeEphemeralChatStorage(messagesStorageKey, JSON.stringify(withDelivery));
      const body: Record<string, unknown> = {
        message: submittedContent,
        ...(submittedPolicy === "reflection_only" ? { actionPolicy: submittedPolicy } : {}),
        sessionId: requestSession,
        historyScope,
        recentMessages,
        stream: true,
        pageContext: pageContext ? {
          pageName: pageContext.pageName,
          data: pageContext.data,
        } : undefined,
      };

      dispatched = true;
      for await (const evt of readSSEStream(
        'leader-chat',
        body,
        session,
        supabaseUrl,
        abortController.signal,
      )) {
        if (!isCurrentRequest()) return;

        if (evt.event === 'delta') {
          setIsLoading(false);
          setIsStreaming(true);
          const text = (evt.data as { text?: unknown } | null)?.text;
          if (typeof text !== "string") throw new Error("stream_incomplete");
          streamingContentRef.current += text;
          setStreamingContent(streamingContentRef.current);
        } else if (evt.event === 'meta') {
          const meta = evt.data as {
            sessionId?: string;
            messageId?: string | null;
            actions?: unknown[];
            contentOverride?: string;
            conversationState?: LeaderConversationState | null;
          };
          if (!meta || typeof meta !== "object" || Array.isArray(meta)
            || typeof meta.sessionId !== "string" || meta.sessionId !== requestSession
            || !(typeof meta.messageId === "string" || meta.messageId === null)
            || (meta.contentOverride !== undefined && typeof meta.contentOverride !== "string")
            || (meta.actions !== undefined && !Array.isArray(meta.actions))) throw new Error("stream_incomplete");
          if (receivedMetadata) continue;
          receivedMetadata = true;
          pendingDeliveryRef.current = null;
          setRecoveryRequired(false);
          setMessages(prev => prev.map(message => message.id === userMessage.id ? { ...message, delivery: undefined } : message));
          setIsStreaming(false);
          setStreamingContent('');
          sessionIdRef.current = meta.sessionId;
          setSessionId(meta.sessionId);
          if (meta.conversationState !== undefined) {
            setConversationState(meta.conversationState ?? null);
          }
          const persisted = typeof meta.messageId === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(meta.messageId);
          const assistantMessage: ChatMessage = {
            id: meta.messageId || crypto.randomUUID(),
            persisted,
            role: 'assistant',
            content: meta.contentOverride ?? streamingContentRef.current,
            createdAt: new Date(),
          };
          setMessages((prev) => [...prev, assistantMessage]);
          streamingContentRef.current = '';
          if (meta.actions?.length) {
            handleActions(meta.actions as VeeraAction[]);
          }
        } else if (evt.event === 'error') {
          const data = evt.data as { error: string; persistedUserMessageId?: unknown };
          if (!data || typeof data !== "object" || typeof data.error !== "string") throw new Error("stream_incomplete");
          serverError = true;
          pendingDeliveryRef.current = null;
          setMessages(prev => prev.map(message => message.id === userMessage.id ? { ...message, delivery: undefined } : message));
          if (Object.prototype.hasOwnProperty.call(data, "persistedUserMessageId")) {
            hasPersistenceReceipt = true;
            if (typeof data.persistedUserMessageId === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.persistedUserMessageId)) confirmedUserMessageId = data.persistedUserMessageId;
          }
          throw new Error(data.error);
        }
      }
      if (!receivedMetadata) throw new Error('stream_incomplete');
    } catch (err: unknown) {
      if (!isCurrentRequest()) return;
      // Metadata is the terminal result; later transport failure cannot undo its receipt.
      if (receivedMetadata) return;

      console.error("Chat error:", err);
      const message = dispatched && !serverError ? "stream_incomplete" : err instanceof Error ? err.message : "Failed to send message";
      if (message === "stream_incomplete") setRecoveryRequired(true);
      else pendingDeliveryRef.current = null;
      setError(message);

      if (message === "stream_incomplete") {
        streamingContentRef.current = '';
        setStreamingContent('');
        toast.error("The response was interrupted before confirmation. Check for a saved answer before sending again.");
      } else if (message === "reflection_only_action_denied") {
        toast.error("Reflection only blocked an action. You can keep reflecting or turn it off for your next message.");
      } else if (message.includes("Rate limit")) {
        toast.error("Too many messages. Please wait a moment and try again.");
      } else if (message.includes("credits")) {
        toast.error("AI service unavailable. Please try again later.");
      } else {
        toast.error("Couldn't reach Veera. Please try again.");
      }

      if (confirmedUserMessageId) {
        setMessages((prev) => prev.map((m) => m.id === userMessage.id ? { ...m, id: confirmedUserMessageId! } : m));
      } else if (message !== "stream_incomplete" && (hasPersistenceReceipt || message !== "reflection_only_action_denied")) {
        // An absent receipt supports the older server's policy-refusal response only.
        setMessages((prev) => prev.filter((m) => m.id !== userMessage.id));
      }
    } finally {
      if (isCurrentRequest()) {
        sendInFlightRef.current = false;
        setIsLoading(false);
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    }
  }, [authLoading, user, historyScope, pageContext, handleActions, messagesStorageKey, supabaseUrl, actionPolicy, sessionStorageKey]);

  const clearChat = useCallback(() => {
    if (isPreview) return;
    requestGenerationRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    streamingContentRef.current = '';
    seededContextualConversationRef.current = null;
    setMessages([]);
    setSessionId(null);
    setConversationState(null);
    setIsLoading(false);
    setIsStreaming(false);
    setStreamingContent('');
    setError(null);
    setHasResolvedInitialMessages(true);
    historyReadyRef.current = true;
    recoveredQuestionRef.current = null;
    setHistoryReadFailed(false);
    pendingDeliveryRef.current = null;
    sendInFlightRef.current = false;
    recoveryInFlightRef.current = false;
    messagesRef.current = [];
    sessionIdRef.current = null;
    setRecoveryRequired(false);
    setCheckingRecovery(false);
    if (sessionStorageKey) removeEphemeralChatStorage(sessionStorageKey);
    if (messagesStorageKey) removeEphemeralChatStorage(messagesStorageKey);
  }, [isPreview, messagesStorageKey, sessionStorageKey]);

  const consumeRecoveredQuestion = useCallback(() => {
    const question = recoveredQuestionRef.current;
    recoveredQuestionRef.current = null;
    return question;
  }, []);

  return {
    messages,
    conversationState,
    isLoading,
    isStreaming,
    streamingContent,
    error,
    canSend: !authLoading && !!user && !isPreview && hasResolvedInitialMessages && !isLoading && !isStreaming && !recoveryRequired,
    deliveryRecovery: { required: recoveryRequired || historyReadFailed, reason: historyReadFailed && !recoveryRequired ? "history_unavailable" : "stream_incomplete", checking: checkingRecovery, check: checkSavedConversation } satisfies ChatDeliveryRecoveryState,
    consumeRecoveredQuestion,
    sendMessage,
    clearChat,
    messagesEndRef,
  };
}
